"use client";

import { useEffect, useMemo, useState } from "react";
import { TrackedLink } from "@/components/analytics/TrackedLink";

const FREE_WEBINAR_START_AT = "2026-08-09T17:00:00.000Z";
const WEBINAR_CTA_URL =
  "https://singingattitude.com/free-webinar?utm_source=singingattitude_uk_homepage&utm_medium=site_banner&utm_campaign=free_webinar_giveaway&utm_content=countdown_banner";

const EVENT_ANNOUNCEMENT = {
  event: "Free Live Online Webinar",
  eyebrow: "FREE LIVE ONLINE WEBINAR",
  title: "Discover What's Really Blocking Your Voice",
  host: "Liuba Doga",
  dateLabel: "Sunday 9 August",
  timeLabel: "6:00pm BST",
  timezoneLabel: "British Summer Time",
  timezone: "Europe/London",
  limitedLine: "Limited live places available.",
  ctaText: "Save My Free Seat",
  ctaUrl: WEBINAR_CTA_URL,
};

type CountdownStatus = "pending" | "active" | "expired";

type CountdownState = {
  status: CountdownStatus;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

function getCountdownState(now: Date, targetDate: Date): CountdownState {
  const remainingMs = targetDate.getTime() - now.getTime();

  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    return {
      status: "expired",
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;

  return {
    status: "active",
    days,
    hours,
    minutes,
    seconds,
  };
}

function CountdownUnit({ value, label, isPending = false }: { value: number; label: string; isPending?: boolean }) {
  return (
    <span className="flex min-w-[3.65rem] flex-col items-center rounded-[14px] border border-white/12 bg-white/8 px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] sm:min-w-[4.25rem] sm:px-3">
      <span className="font-serif text-2xl font-semibold leading-none text-[#f2d47d] sm:text-[1.7rem]">
        {isPending ? "--" : String(value).padStart(2, "0")}
      </span>
      <span className="mt-1 text-[0.58rem] font-bold uppercase tracking-[0.16em] text-white/68 sm:text-[0.62rem]">
        {label}
      </span>
    </span>
  );
}

export function EventAnnouncement() {
  const targetDate = useMemo(() => new Date(FREE_WEBINAR_START_AT), []);
  const [countdown, setCountdown] = useState<CountdownState>({
    status: "pending",
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const isCountdownPending = countdown.status === "pending";

  useEffect(() => {
    const updateCountdown = () => {
      setCountdown(getCountdownState(new Date(), targetDate));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(intervalId);
  }, [targetDate]);

  // The server cannot confirm a live countdown. Keep the promotion hidden until
  // the browser checks the date so past events never flash during hydration.
  if (countdown.status !== "active") {
    return null;
  }

  return (
    <section
      aria-label={`${EVENT_ANNOUNCEMENT.event}: ${EVENT_ANNOUNCEMENT.title}`}
      className="px-3 pt-4 sm:px-5 sm:pt-5 lg:px-8"
    >
      <div className="mx-auto w-full max-w-[92rem]">
        <div className="relative overflow-hidden rounded-[24px] border border-[#d6aa43]/36 bg-[#071c27] px-4 py-4 text-white shadow-[0_22px_70px_rgba(6,21,27,0.24)] ring-1 ring-white/8 sm:px-5 lg:px-6">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_0%,rgba(217,178,78,0.18),transparent_20rem),radial-gradient(circle_at_100%_20%,rgba(34,95,88,0.28),transparent_22rem)]"
            aria-hidden="true"
          />
          <div className="relative grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto_auto] lg:items-center">
            <div className="min-w-0">
              <p className="text-[0.68rem] font-black uppercase tracking-[0.24em] text-[#f2d47d]">
                {EVENT_ANNOUNCEMENT.eyebrow}
              </p>
              <h2 className="mt-1 font-serif text-[1.55rem] font-semibold leading-tight text-white sm:text-3xl">
                {EVENT_ANNOUNCEMENT.title}
              </h2>
              <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold leading-relaxed text-white/78 sm:text-[0.95rem]">
                <span>{EVENT_ANNOUNCEMENT.dateLabel}</span>
                <span aria-hidden="true">•</span>
                <span>
                  {EVENT_ANNOUNCEMENT.timeLabel} / {EVENT_ANNOUNCEMENT.timezoneLabel}
                </span>
                <span aria-hidden="true">•</span>
                <span>Online with {EVENT_ANNOUNCEMENT.host}</span>
                <span className="sr-only">Timezone: {EVENT_ANNOUNCEMENT.timezone}</span>
              </p>
            </div>

            <div className="min-w-0" aria-live="polite">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.22em] text-white/64">Starts in</p>
              <div className="flex max-w-full flex-wrap gap-2">
                <CountdownUnit value={countdown.days} label="Days" isPending={isCountdownPending} />
                <CountdownUnit value={countdown.hours} label="Hours" isPending={isCountdownPending} />
                <CountdownUnit value={countdown.minutes} label="Mins" isPending={isCountdownPending} />
                <CountdownUnit value={countdown.seconds} label="Secs" isPending={isCountdownPending} />
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-2 lg:min-w-[11.5rem] lg:items-end">
              <TrackedLink
                href={EVENT_ANNOUNCEMENT.ctaUrl}
                eventName="uk_home_webinar_countdown_click"
                eventProperties={{
                  source_page: "home",
                  source_section: "webinar_countdown_banner",
                  webinar_slug: "discover-whats-blocking-your-voice-2026-08-09",
                }}
                aria-label={`${EVENT_ANNOUNCEMENT.ctaText} for ${EVENT_ANNOUNCEMENT.title}`}
                className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-[#f2d47d] px-5 py-3 text-center text-sm font-black text-[#071c27] shadow-[0_16px_40px_rgba(217,178,78,0.24)] transition hover:bg-[#ffe39a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#f2d47d] lg:w-auto"
              >
                {EVENT_ANNOUNCEMENT.ctaText}
              </TrackedLink>
              <p className="text-center text-xs font-semibold text-white/66 lg:text-right">
                {EVENT_ANNOUNCEMENT.limitedLine}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
